// The Expenses page (Session ATE, engineer expenses and mileage). Office/admin review of engineer
// claims: approve, reject (with an optional reason) or delete (RULE 1). Filter by engineer, status
// and date. A SIMPLE LIST, not a calendar or ledger view, matching LeavePage's own reasoning for this
// scale of business.
//
// AN APPROVED, JOB-LINKED CLAIM feeds that job's cost figure (ServiceRecordPanel's "Expenses" stat,
// via server/src/modules/jobsheets/profitability.ts otherCostsPence). A GENERAL claim (no job) never
// distorts any one job's margin -- it has no job to attach to -- so it is totalled separately here, for
// the same window as the office is currently viewing, rather than folded into a per-job figure.
//
// SESSION AUJ (design system Session 2, pilot): the page now speaks the shared design system. PageHeader,
// ListToolbar (search, the engineer / status / date filters in its slot, a result count and Clear), a
// sortable `.ele-list-table` with a card list on a phone (the AQU pattern), Button, the status registry's
// `expense` domain (the one CHANGE cell here: SUBMITTED reads amber, "waiting on the office", where it was
// a neutral outline), Loader, EmptyState and lib/format dates (an expense date prints "5 Sep 2026" where it
// printed the ISO "2026-09-05"). Presentation only: every route, call, permission and word of copy is as
// before. The server filters stay server filters; search and sort run client-side over what they returned.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { expensesApi, EXPENSE_TYPE_LABEL, EXPENSE_STATUSES, type Expense, type ExpenseStatus } from "../../lib/expenses";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";

interface EngineerOption {
  id: string;
  name: string;
}

// Search and sort run client-side over the rows the server returned for the engineer / status / date
// filters. Status sorts in its working order (waiting, then approved, then rejected), never alphabetically.
const STATUS_RANK: Record<ExpenseStatus, number> = { submitted: 0, approved: 1, rejected: 2 };
const EXPENSE_SEARCH: SearchAccessors<Expense> = [
  (e) => e.engineerName,
  (e) => e.description,
  (e) => e.jobLabel ?? "General (no job)",
  (e) => EXPENSE_TYPE_LABEL[e.type],
  (e) => statusEntry("expense", e.status).label,
];
const EXPENSE_SORT: SortAccessors<Expense> = {
  engineer: (e) => e.engineerName,
  type: (e) => EXPENSE_TYPE_LABEL[e.type],
  date: (e) => e.date,
  job: (e) => e.jobLabel ?? "",
  amount: (e) => e.amountPence,
  status: (e) => STATUS_RANK[e.status],
};
const SORT_OPTIONS = [
  { key: "engineer", label: "Engineer" },
  { key: "date", label: "Date" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
];

function gbpPence(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}

export function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [engineerFilter, setEngineerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [engineers, setEngineers] = useState<EngineerOption[]>([]);
  // Below md the list renders as cards (decided once at mount; jsdom has no matchMedia, so tests get the table).
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("expenses");

  const [rowError, setRowError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [rejecting, setRejecting] = useState<Expense | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<Expense | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await expensesApi.list({
        engineerId: engineerFilter || undefined,
        status: statusFilter || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setExpenses(res.expenses);
      setLoadError(null);
    } catch (err) {
      setLoadError(friendlyApiError(err, "Could not load expenses."));
    }
  }, [engineerFilter, statusFilter, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.scheduling
      .engineers()
      .then((rows) => setEngineers(rows.map((e) => ({ id: e.id, name: e.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setEngineers([]));
  }, []);

  const loaded = useMemo(() => expenses ?? [], [expenses]);
  const visible = useMemo(
    () => sortRows(filterRows(loaded, search, EXPENSE_SEARCH), EXPENSE_SORT, sort),
    [loaded, search, sort.sortKey, sort.sortDir],
  );

  // A simple monthly-style total for GENERAL (no job) approved claims in the current filter window --
  // the "reported separately, not folded into any one job's margin" the session scope asks for. Not a
  // full overhead-allocation model: just the sum the office would otherwise have to add up by hand.
  const generalApprovedTotalPence = useMemo(
    () => visible.filter((e) => e.jobId == null && e.status === "approved").reduce((sum, e) => sum + e.amountPence, 0),
    [visible],
  );

  async function approve(e: Expense) {
    setRowError(null);
    setBusyId(e.id);
    try {
      await expensesApi.approve(e.id);
      await load();
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not approve this expense. Please try again."));
    } finally {
      setBusyId(null);
    }
  }

  function openReject(e: Expense) {
    setRejectReason("");
    setRejectError(null);
    setRejecting(e);
  }

  async function runReject() {
    if (!rejecting) return;
    setConfirmBusy(true);
    setRejectError(null);
    try {
      await expensesApi.reject(rejecting.id, rejectReason.trim() || undefined);
      setRejecting(null);
      await load();
    } catch (err) {
      setRejectError(friendlyApiError(err, "Could not reject this expense. Please try again."));
    } finally {
      setConfirmBusy(false);
    }
  }

  async function runDelete() {
    if (!confirmDelete) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await expensesApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setConfirmError(friendlyApiError(err, "That did not work. Please try again."));
    } finally {
      setConfirmBusy(false);
    }
  }

  function clearAll() {
    setSearch("");
    setEngineerFilter("");
    setStatusFilter("");
    setFrom("");
    setTo("");
  }

  function rowActions(e: Expense) {
    return (
      <>
        {e.status === "submitted" && (
          <>
            <Button variant="secondary" size="sm" onClick={() => void approve(e)} disabled={busyId === e.id} aria-label={`Approve ${e.engineerName}'s expense`}>
              Approve
            </Button>
            <Button variant="ghost" size="sm" onClick={() => openReject(e)} disabled={busyId === e.id} aria-label={`Reject ${e.engineerName}'s expense`}>
              Reject
            </Button>
          </>
        )}
        {e.status === "approved" && (
          <Button variant="ghost" size="sm" onClick={() => openReject(e)} disabled={busyId === e.id}>
            Reject
          </Button>
        )}
        {e.status !== "approved" && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setConfirmError(null);
              setConfirmDelete(e);
            }}
            aria-label={`Delete ${e.engineerName}'s expense`}
          >
            Delete
          </Button>
        )}
      </>
    );
  }

  function rowDetails(e: Expense) {
    return (
      <>
        {e.receiptUrl && (
          <a href={e.receiptUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-ele-blue underline">
            View receipt
          </a>
        )}
        {e.status === "rejected" && e.rejectionReason && <p className="mt-1 text-xs text-ele-danger">Rejected: {e.rejectionReason}</p>}
        {e.decidedByName && <p className="mt-1 text-xs text-ele-body opacity-70">Decided by {e.decidedByName}</p>}
      </>
    );
  }

  const mileageNote = (e: Expense) =>
    e.type === "mileage" && e.milesClaimed != null ? ` (${e.milesClaimed} mi at ${e.mileageRatePencePerMile}p)` : "";

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader
        className="mb-4"
        title="Expenses"
        subtitle="Mileage, materials bought on the fly, and other costs an engineer claims. Approving a claim linked to a job adds it to that job's cost; a general claim (no job) is a company cost with no job to attach to, totalled below instead."
      />

      <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Expenses">
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search engineer, description or job..."
          searchLabel="Search expenses"
          resultCount={{ shown: visible.length, total: loaded.length, noun: "expenses" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={clearAll}
          filters={
            <>
              <select value={engineerFilter} onChange={(e) => setEngineerFilter(e.target.value)} className={`${inputCls} w-auto`} aria-label="Filter by engineer">
                <option value="">Every engineer</option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ExpenseStatus | "")} className={`${inputCls} w-auto`} aria-label="Filter by status">
                <option value="">Every status</option>
                {EXPENSE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {statusEntry("expense", s).label}
                  </option>
                ))}
              </select>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} w-auto`} aria-label="From date" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} w-auto`} aria-label="To date" />
            </>
          }
        />

        {generalApprovedTotalPence > 0 && (
          <p className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong">
            General (no job) approved expenses in this view: <span className="font-semibold">{gbpPence(generalApprovedTotalPence)}</span>
          </p>
        )}

        {loadError && (
          <p role="alert" className="text-sm text-ele-danger">
            {loadError}
          </p>
        )}
        {rowError && (
          <p role="alert" className="mb-2 text-sm text-ele-danger">
            {rowError}
          </p>
        )}
        {!loadError && expenses === null && <Loader label="Loading the expenses..." />}
        {expenses !== null && loaded.length === 0 && <EmptyState message="No expenses to show." />}
        {expenses !== null && loaded.length > 0 && visible.length === 0 && (
          <EmptyState role="status" message={`No expenses match "${search.trim()}".`} />
        )}

        {visible.length > 0 &&
          (mobileCards ? (
            <MobileCardList label="Expenses">
              {visible.map((e) => (
                <MobileListCard
                  key={e.id}
                  primary={e.engineerName}
                  secondary={e.description}
                  meta={`${formatDate(e.date)} · ${e.jobLabel ?? "General (no job)"}${mileageNote(e)}`}
                  right={
                    <>
                      <span className="font-semibold text-ele-strong">{gbpPence(e.amountPence)}</span>
                      <StatusPill domain="expense" status={e.status} />
                    </>
                  }
                  details={rowDetails(e)}
                  actions={rowActions(e)}
                />
              ))}
            </MobileCardList>
          ) : (
            <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  <col style={{ width: "14%" }} />
                  <col className="max-md:hidden" style={{ width: "9%" }} />
                  <col style={{ width: "11%" }} />
                  <col className="max-md:hidden" style={{ width: "14%" }} />
                  <col className="max-md:hidden" style={{ width: "21%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
                  <tr>
                    <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                    <SortableHeader label="Date" colKey="date" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Job" colKey="job" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                    <th className="px-3 py-2 max-md:hidden">Description</th>
                    <SortableHeader label="Amount" colKey="amount" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                    <th className="px-3 py-2 cell-actions">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((e) => (
                    <tr key={e.id} className="border-t border-ele-hairline align-top" data-testid="expense-row">
                      <td className="px-3 py-2 font-semibold text-ele-strong">{e.engineerName}</td>
                      <td className="px-3 py-2 text-ele-body max-md:hidden">{EXPENSE_TYPE_LABEL[e.type]}</td>
                      <td className="px-3 py-2 text-ele-body">{formatDate(e.date)}</td>
                      <td className="px-3 py-2 text-ele-body max-md:hidden">{e.jobLabel ?? "General (no job)"}</td>
                      <td className="cell-wrap px-3 py-2 text-xs text-ele-body max-md:hidden">
                        <p>
                          {e.description}
                          {mileageNote(e)}
                        </p>
                        {rowDetails(e)}
                      </td>
                      <td className="px-3 py-2 font-semibold text-ele-strong">{gbpPence(e.amountPence)}</td>
                      <td className="cell-actions px-3 py-2">
                        <StatusPill domain="expense" status={e.status} />
                      </td>
                      <td className="cell-actions px-3 py-2">
                        <div className="flex flex-wrap justify-end gap-2">{rowActions(e)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {rejecting && (
          <ModalShell ariaLabel="Reject expense" onClose={() => !confirmBusy && setRejecting(null)} width="max-w-md">
            <h2 className="mb-4 font-heading text-xl text-ele-strong">Reject this expense?</h2>
            <p className="mb-3 text-sm text-ele-body">
              {rejecting.engineerName}'s {EXPENSE_TYPE_LABEL[rejecting.type].toLowerCase()} claim for {gbpPence(rejecting.amountPence)} will no longer count toward job costing or the
              monthly total.
            </p>
            <label className={labelCls} htmlFor="expense-reject-reason">
              Reason (optional)
            </label>
            <textarea id="expense-reject-reason" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className={inputCls} />
            {rejectError && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
                {rejectError}
              </p>
            )}
            <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
              <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={() => setRejecting(null)} disabled={confirmBusy}>
                Cancel
              </Button>
              <Button variant="destructive" className={MODAL_ACTION_BTN} onClick={() => void runReject()} disabled={confirmBusy}>
                {confirmBusy ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </ModalShell>
        )}

        {confirmDelete && (
          <ConfirmDeleteModal
            open
            variant="destructive"
            title={`Delete ${confirmDelete.engineerName}'s expense?`}
            message="This deletes the expense claim for good. It cannot be undone."
            confirmLabel="Delete"
            busy={confirmBusy}
            error={confirmError}
            onConfirm={() => void runDelete()}
            onCancel={() => !confirmBusy && setConfirmDelete(null)}
          />
        )}
      </section>
    </div>
  );
}
